import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import Select from "react-select";
import { addPupilsToCategory } from "api/pupils/addPupilsToCategory";
import { getSchools } from "redux/actions/setSchoolActions";
import { useFetchPupilsByCategory } from "hooks/useQuery/useFetchPupilsByCategory";

// Кастомные стили для react-select
const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        padding: '0.25rem',
        border: '1px solid #ddd',
        borderRadius: '8px',
        fontSize: '16px',
        borderColor: state.isFocused ? '#4a90e2' : '#ddd',
        boxShadow: 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#4a90e2' : '#ddd',
        },
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: '16px',
        padding: '0.75rem',
        backgroundColor: state.isSelected ? '#4a90e2' : state.isFocused ? '#f5f5f5' : 'white',
        color: state.isSelected ? 'white' : '#122443',
        cursor: 'pointer',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#999',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#122443',
    }),
};

export const AddPupilsToCategoryModal = ({ categoryId, onClose }) => {
    const dispatch = useDispatch();
    const { schools } = useSelector((state) => state.school);
    const { pupils: pupilsInCategory } = useFetchPupilsByCategory(categoryId);
    const [selectedSchoolId, setSelectedSchoolId] = useState("");
    const [selectedClassId, setSelectedClassId] = useState("");
    const [selectedPupilIds, setSelectedPupilIds] = useState([]);
    const [classes, setClasses] = useState([]);
    const [pupils, setPupils] = useState([]);
    const [isLoadingClasses, setIsLoadingClasses] = useState(false);
    const [isLoadingPupils, setIsLoadingPupils] = useState(false);

    useEffect(() => {
        if (!schools?.length) {
            dispatch(getSchools());
        }
    }, [dispatch, schools]);

    const addMutation = useMutation({
        mutationFn: () => addPupilsToCategory(categoryId, selectedPupilIds),
        onSuccess: () => {
            onClose();
        },
    });

    const handleSchoolChange = async (selectedOption) => {
        const schoolId = selectedOption?.value || "";
        setSelectedSchoolId(schoolId);
        setSelectedClassId("");
        setSelectedPupilIds([]);
        setPupils([]);

        if (schoolId) {
            setIsLoadingClasses(true);
            try {
                const response = await axios.get(`/schools/${schoolId}/classes`);
                setClasses(response.data);
            } catch (error) {
                console.error("Ошибка загрузки классов:", error);
            } finally {
                setIsLoadingClasses(false);
            }
        } else {
            setClasses([]);
        }
    };

    const handleClassChange = async (selectedOption) => {
        const classId = selectedOption?.value || "";
        setSelectedClassId(classId);
        setSelectedPupilIds([]);

        if (classId) {
            setIsLoadingPupils(true);
            try {
                const response = await axios.get(`/classes/${classId}/pupils`);
                // API возвращает массив напрямую, а не объект с полем pupils
                const allPupils = Array.isArray(response.data) ? response.data : [];
                
                // Получаем ID учеников, которые уже в этой категории
                const pupilIdsInCategory = pupilsInCategory.map(p => p._id);
                
                // Фильтруем учеников - оставляем только тех, кто НЕ в категории
                const availablePupils = allPupils.filter(pupil => 
                    !pupilIdsInCategory.includes(pupil._id)
                );
                
                setPupils(availablePupils);
            } catch (error) {
                console.error("Ошибка загрузки учеников:", error);
            } finally {
                setIsLoadingPupils(false);
            }
        } else {
            setPupils([]);
        }
    };

    const handlePupilToggle = (pupilId) => {
        setSelectedPupilIds((prev) =>
            prev.includes(pupilId)
                ? prev.filter((id) => id !== pupilId)
                : [...prev, pupilId]
        );
    };

    const handleSelectAll = () => {
        if (selectedPupilIds.length === pupils.length) {
            setSelectedPupilIds([]);
        } else {
            setSelectedPupilIds(pupils.map((p) => p._id));
        }
    };

    const handleSubmit = () => {
        if (selectedPupilIds.length > 0) {
            addMutation.mutate();
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Добавить учеников в целевую группу</h2>
                    <button className="modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="modal-body" style={{
                    minHeight: "500px"
                }}>
                    <div className="form-group">
                        <label>Выберите школу:</label>
                        <Select
                            value={schools.find(s => s._id === selectedSchoolId) ? { value: selectedSchoolId, label: schools.find(s => s._id === selectedSchoolId).name } : null}
                            onChange={handleSchoolChange}
                            options={schools.map(school => ({ value: school._id, label: school.name }))}
                            placeholder="-- Выберите школу --"
                            isClearable
                            isSearchable
                            styles={customSelectStyles} // eslint-disable-line no-undef
                        />
                    </div>

                    {selectedSchoolId && (
                        <div className="form-group">
                            <label>Выберите класс:</label>
                            <Select
                                value={classes.find(c => c._id === selectedClassId) ? { value: selectedClassId, label: `${classes.find(c => c._id === selectedClassId).number} ${classes.find(c => c._id === selectedClassId).letter}` } : null}
                                onChange={handleClassChange}
                                options={classes.map(cls => ({ value: cls._id, label: `${cls.number} ${cls.letter}` }))}
                                placeholder="-- Выберите класс --"
                                isClearable
                                isSearchable
                                isDisabled={isLoadingClasses}
                                isLoading={isLoadingClasses}
                                styles={customSelectStyles} // eslint-disable-line no-undef
                            />
                        </div>
                    )}

                    {selectedClassId && (
                        <div className="form-group">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <label>Выберите учеников:</label>
                                {pupils.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn_secondary btn_small"
                                        onClick={handleSelectAll}
                                    >
                                        {selectedPupilIds.length === pupils.length ? "Снять все" : "Выбрать все"}
                                    </button>
                                )}
                            </div>

                            {isLoadingPupils ? (
                                <p>Загрузка учеников...</p>
                            ) : pupils.length > 0 ? (
                                <div className="pupils-list">
                                    {pupils.map((pupil) => (
                                        <label key={pupil._id} className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={selectedPupilIds.includes(pupil._id)}
                                                onChange={() => handlePupilToggle(pupil._id)}
                                            />
                                            <span>
                                                {pupil.surname} {pupil.name} {pupil.patronymic}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p>В этом классе нет учеников</p>
                            )}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn btn_secondary" onClick={onClose}>
                        Отмена
                    </button>
                    <button
                        className="btn btn_primary"
                        onClick={handleSubmit}
                        disabled={selectedPupilIds.length === 0 || addMutation.isLoading}
                    >
                        {addMutation.isLoading ? "Сохранение..." : `Сохранить (${selectedPupilIds.length})`}
                    </button>
                </div>
            </div>
        </div>
    );
};
